// WHAT: Yazio Dashboard Component (Organism)
// WHY:  Main dashboard displaying daily nutrition overview with stats, meals, and quick metrics
// DOMAIN: Health → Yazio
// DEPENDENCIES: DailyStatsCard, MealsOverview, UI components, Lucide icons
// RISKS:
//   - NOTE: Date formatting uses en-US locale (hardcoded)
//   - NOTE: Error handling shows error card but doesn't retry automatically
//   - NOTE: Last updated time shows current time (not actual data timestamp)

import type { DailyCaloriesResponse, YazioDashboardProperties } from "../../types";

import { Activity, AlertCircle, Clock, RefreshCw, TrendingUp } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageLoader } from "@/components/ui/PageLoader";

import { DailyStatsCard } from "../molecules/DailyStatsCard";
import { MealsOverview } from "./MealsOverview";

/**
 * WHAT:
 *   Main dashboard organism displaying comprehensive daily nutrition overview.
 *
 * WHY:
 *   Provides central hub for nutrition tracking with stats, meal progress,
 *   and quick metrics. Handles loading and error states gracefully.
 *
 * PROPS:
 *   - data: DailyCaloriesResponse | null — Daily nutrition data (calories, meals, water, steps)
 *   - isLoading: boolean — Loading state
 *   - error: string | null — Error message if data load failed
 *   - onRefresh: () => void — Callback to refresh data
 *   - onClearError: () => void — Callback to dismiss error
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Shows error card with retry/dismiss buttons if error exists
 *   - Shows PageLoader during loading state
 *   - Shows empty state card if no data (with load button)
 *   - Displays header with date and refresh button
 *   - Shows DailyStatsCard with calories, water, steps
 *   - Displays MealsOverview with all meal cards
 *   - Shows quick stats grid: calories remaining, goal progress, last updated
 *   - Date formatting uses en-US locale (hardcoded)
 *
 * RISKS:
 *   - NOTE: Date formatting is hardcoded to en-US (not internationalized)
 *   - NOTE: Last updated shows current time (not actual data timestamp)
 *   - NOTE: Goal progress calculation may divide by zero if caloriesGoal is 0
 *   - NOTE: Error state doesn't auto-retry (user must click button)
 *
 * @param root0 - Component props
 *
 * @param root0.data - Daily nutrition data
 *
 * @param root0.isLoading - Loading state
 *
 * @param root0.error - Error message if data load failed
 *
 * @param root0.onRefresh - Callback to refresh data
 *
 * @param root0.onClearError - Callback to dismiss error
 *
 * @param root0.className - Additional CSS classes
 *
 * @returns {JSX.Element} React component rendering the Yazio dashboard
 *
 */
export function YazioDashboard({
  className,
  data,
  error,
  isLoading,
  onClearError,
  onRefresh,
}: Readonly<YazioDashboardProperties>) {
  // Wrapper functions for callbacks to ensure type safety
  // Reason: TypeScript needs explicit typing to recognize the callback types
  const handleRefresh: () => void = (): void => {
    (onRefresh as () => void)();
  };
  const handleClearError: () => void = (): void => {
    (onClearError as () => void)();
  };

  if (error) {
    return (
      <div className={`space-y-6 ${className ?? ""}`}>
        <Card className="bg-red-900/20 border-red-500/20 text-white">
          <div className="p-6">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <div>
                <h3 className="text-lg font-semibold text-red-400">Error Loading Data</h3>
                <p className="text-red-300 mt-1">{error}</p>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleRefresh} size="sm" variant="outline">
                Try Again
              </Button>
              <Button onClick={handleClearError} size="sm" variant="outline">
                Dismiss
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return <PageLoader message="Loading your nutrition data..." size="lg" withBackdrop={false} />;
  }

  if (!data) {
    return (
      <div className={`space-y-6 ${className ?? ""}`}>
        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-6 text-center">
            <h3 className="text-lg font-semibold text-white mb-2">No Data Available</h3>
            <p className="text-slate-400 mb-4">
              We couldn&apos;t load your nutrition data. Make sure your Yazio credentials are
              configured.
            </p>
            <Button onClick={handleRefresh} variant="primary">
              Load Data
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Type guard: data is guaranteed to be DailyCaloriesResponse here
  if (!("caloriesGoal" in data)) {
    return null;
  }
  const nutritionData: DailyCaloriesResponse = data;

  return (
    <div className={`space-y-6 ${className ?? ""}`}>
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Nutrition Dashboard</h1>
          <p className="text-slate-400 mt-1">
            Your daily nutrition overview for{" "}
            {new Date(nutritionData.date).toLocaleDateString("en-US")}
          </p>
        </div>
        <Button disabled={isLoading} onClick={handleRefresh} size="sm" variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Daily Stats Overview */}
      <DailyStatsCard
        caloriesGoal={nutritionData.caloriesGoal}
        steps={nutritionData.steps}
        totalCalories={nutritionData.totalCalories}
        waterIntake={nutritionData.waterIntake}
      />

      {/* Meals Overview */}
      <MealsOverview meals={nutritionData.meals} />

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Calories Remaining</div>
                <div className="text-xl font-semibold text-white">
                  {nutritionData.caloriesRemaining > 0
                    ? Math.round(nutritionData.caloriesRemaining)
                    : 0}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Goal Progress</div>
                <div className="text-xl font-semibold text-white">
                  {nutritionData.caloriesGoal > 0
                    ? Math.round((nutritionData.totalCalories / nutritionData.caloriesGoal) * 100)
                    : 0}
                  %
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Last Updated</div>
                <div className="text-xl font-semibold text-white">
                  {new Date().toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
